import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Avatar,
  Paper,
} from '@mui/material';
import {
  SmartToy as BotIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import type { ChatMessage as ChatMessageType } from '../../types/help.types';
import { SuggestedActions } from './SuggestedActions';

interface ChatMessageProps {
  message: ChatMessageType;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        gap: 1.5,
      }}
    >
      {/* Avatar */}
      <Avatar
        sx={{
          width: 32,
          height: 32,
          bgcolor: isUser ? 'secondary.main' : 'primary.main',
        }}
      >
        {isUser ? <PersonIcon fontSize="small" /> : <BotIcon fontSize="small" />}
      </Avatar>

      {/* Message Content */}
      <Box sx={{ maxWidth: '85%' }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: isUser ? 'primary.main' : 'grey.100',
            color: isUser ? 'primary.contrastText' : 'text.primary',
            borderRadius: 2,
            borderTopRightRadius: isUser ? 0 : 2,
            borderTopLeftRadius: isUser ? 2 : 0,
          }}
        >
          {/* Message Text */}
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              '& strong': { fontWeight: 600 },
              '& code': {
                bgcolor: isUser ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                fontFamily: 'monospace',
                fontSize: '0.85em',
              },
            }}
          >
            {formatMessageContent(message.content)}
          </Typography>
        </Paper>

        {/* Timestamp */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            mt: 0.5,
            textAlign: isUser ? 'right' : 'left',
          }}
        >
          {formatDistanceToNow(new Date(message.timestamp), { addSuffix: true })}
        </Typography>

        {/* Suggested Actions */}
        {!isUser && message.suggestedActions && message.suggestedActions.length > 0 && (
          <Box mt={1}>
            <SuggestedActions actions={message.suggestedActions} />
          </Box>
        )}

        {/* Related Articles */}
        {!isUser && message.relatedArticles && message.relatedArticles.length > 0 && (
          <Box mt={1.5}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Related articles:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
              {message.relatedArticles.map((articleSlug) => (
                <Paper
                  key={articleSlug}
                  sx={{
                    px: 1,
                    py: 0.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Typography variant="caption">{articleSlug}</Typography>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * Format message content with basic markdown support
 */
function formatMessageContent(content: string): React.ReactNode {
  // Split by numbered lists
  const parts = content.split(/(\d+\.\s)/);

  if (parts.length === 1) {
    return content;
  }

  const elements: React.ReactNode[] = [];
  let currentText = '';

  parts.forEach((part, index) => {
    if (/^\d+\.\s$/.test(part)) {
      if (currentText) {
        elements.push(currentText);
        currentText = '';
      }
      elements.push(
        <React.Fragment key={index}>
          <br />
          <strong>{part}</strong>
        </React.Fragment>
      );
    } else {
      currentText += part;
    }
  });

  if (currentText) {
    elements.push(currentText);
  }

  return elements;
}

export default ChatMessage;
